import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { t } from '../../i18n/pt';
import { formatEur } from '../../lib/format';
import { useCartStore } from '../../stores/cartStore';
import { Button } from '../../components/ui/Button';
import styles from './CartPage.module.scss';

export default function CartPage(): JSX.Element {
  const items = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const subtotal = useCartStore((s) => s.getSubtotal());
  const applyCoupon = useCartStore((s) => s.applyCoupon);
  const removeCoupon = useCartStore((s) => s.removeCoupon);
  const appliedCoupon = useCartStore((s) => s.appliedCoupon);
  const couponError = useCartStore((s) => s.couponError);

  const [couponInput, setCouponInput] = useState('');

  const discount = appliedCoupon?.discountAmount ?? 0;
  const total = Math.max(0, subtotal - discount);

  return (
    <>
      <SEO title="Carrinho" noindex />
      <div className={`container ${styles.page}`}>
        <h1 className={styles.title}>{t.cart.title}</h1>
        {items.length === 0 ? (
          <div className={styles.empty}>
            <img
              src="/cartemptystate.svg"
              alt=""
              aria-hidden
              className={styles.emptyIllustration}
            />
            <div className={styles.emptyContent}>
              <p className={styles.emptyMessage}>{t.cart.empty}</p>
              <Button variant="primary" size="lg" to="/produtos">
                {t.cart.shopNow}
              </Button>
            </div>
          </div>
        ) : (
          <div className={styles.layout}>
            <ul className={styles.list}>
              {items.map((i) => (
                <li key={i.variantId} className={styles.item}>
                  {i.imageUrl ? (
                    <img src={i.imageUrl} alt="" />
                  ) : (
                    <div className={styles.ph} />
                  )}
                  <div className={styles.info}>
                    <Link to={`/produtos/${i.slug}`} className={styles.itemName}>
                      {i.name}
                    </Link>
                    <small>
                      {i.size} · {i.colour}
                    </small>
                    <div className={styles.qtyRow}>
                      <input
                        type="number"
                        min={0}
                        max={i.maxStock}
                        value={i.quantity}
                        onChange={(e) => updateQuantity(i.variantId, Number(e.target.value))}
                        aria-label={t.product.quantity}
                      />
                      <Button variant="link" onClick={() => removeItem(i.variantId)}>
                        {t.cart.remove}
                      </Button>
                    </div>
                  </div>
                  <div className={styles.price}>{formatEur(i.price * i.quantity)}</div>
                </li>
              ))}
            </ul>

            <aside className={styles.summary}>
              <div className={styles.coupon}>
                {appliedCoupon ? (
                  <>
                    <p>
                      Cupão: <strong>{appliedCoupon.code}</strong>
                    </p>
                    <Button variant="link" onClick={removeCoupon}>
                      Remover
                    </Button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder={t.cart.couponPlaceholder}
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    />
                    <Button
                      variant="secondary"
                      onClick={() => {
                        if (couponInput) void applyCoupon(couponInput);
                      }}
                    >
                      {t.cart.applyCoupon}
                    </Button>
                    {couponError ? <p className={styles.error}>{couponError}</p> : null}
                  </>
                )}
              </div>

              <dl>
                <dt>{t.cart.subtotal}</dt>
                <dd>{formatEur(subtotal)}</dd>
                {discount > 0 ? (
                  <>
                    <dt>{t.cart.discount}</dt>
                    <dd>-{formatEur(discount)}</dd>
                  </>
                ) : null}
                <dt className={styles.totalLabel}>{t.cart.total}</dt>
                <dd className={styles.totalValue}>{formatEur(total)}</dd>
              </dl>
              <p className={styles.note}>Portes calculados no checkout.</p>
              <Button variant="primary" size="lg" fullWidth to="/checkout">
                {t.cart.checkout}
              </Button>
            </aside>
          </div>
        )}
      </div>
    </>
  );
}
